import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
} from "react";

const PiPContext = createContext(undefined);

export function PiPProvider({ children }) {
    const isSupported = "documentPictureInPicture" in window;

    const [pipWindow, setPipWindow] = useState(null);

    const closePipWindow = useCallback(() => {
        if (pipWindow != null) {
            pipWindow.close();
            setPipWindow(null);
        }
    }, [pipWindow]);

    const requestPipWindow = useCallback(
        async (width, height) => {
            if (pipWindow != null) {
                return;
            }

            // Document Picture-in-Picture requires a user activation (e.g., a click)
            const hasUserActivation =
                (navigator.userActivation && navigator.userActivation.isActive) ||
                (typeof document.hasTransientActivation === "boolean" && document.hasTransientActivation);

            if (!hasUserActivation) {
                // Avoid unhandled promise rejections when called without a user gesture
                console.warn("Document PiP requires user activation (click).");
                return null;
            }

            let pip;
            try {
                pip = await window.documentPictureInPicture.requestWindow({
                    width,
                    height,
                });
            } catch (err) {
                // Gracefully handle NotAllowedError or other failures
                console.warn("Unable to open Document PiP window:", err?.message || err);
                return null;
            }

            pip.addEventListener("pagehide", () => {
                setPipWindow(null);
            });

            [...document.styleSheets].forEach((styleSheet) => {
                try {
                    const cssRules = [...styleSheet.cssRules]
                        .map((rule) => rule.cssText)
                        .join("");
                    const style = document.createElement("style");
                    style.textContent = cssRules;
                    pip.document.head.appendChild(style);
                } catch (e) {
                    if (!styleSheet.href) return;

                    const link = document.createElement("link");
                    link.rel = "stylesheet";
                    link.type = styleSheet.type;
                    link.media = styleSheet.media.toString();
                    link.href = styleSheet.href;
                    pip.document.head.appendChild(link);
                }
            });

            setPipWindow(pip);
            return pip;
        },
        [pipWindow]
    );

    const value = useMemo(() => {
        return {
            isSupported,
            pipWindow,
            requestPipWindow,
            closePipWindow,
        };
    }, [isSupported, pipWindow, requestPipWindow, closePipWindow]);

    return <PiPContext.Provider value={value}>{children}</PiPContext.Provider>;
}

export default PiPContext;